import { useId, type ComponentPropsWithoutRef, type ElementType, type ReactNode } from 'react';
import { Icon } from './Icon.js';
import type { IconData } from './icon-data.js';
import { iconExpand } from './generated/icons.js';
import { cx, useControllable } from './util.js';

/** An error line under a control or a form. It is announced when it appears. */
export function ErrorText({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <p id={id} role="alert" className="k-error k-t-body-sm">
      {children}
    </p>
  );
}

/** Controls that belong together under one legend, such as a choice of cards. */
export function FieldGroup({
  legend,
  description,
  children,
  className,
}: {
  legend: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={cx('k-fieldgroup', className)}>
      <legend className="k-t-label-sm k-muted">{legend}</legend>
      {description === undefined ? null : <p className="k-t-body-sm k-muted">{description}</p>}
      {children}
    </fieldset>
  );
}

/** Something that opens and closes in place. A button with `aria-expanded`, not a `<details>`. */
export function Disclosure({
  summary,
  children,
  open,
  defaultOpen = false,
  onOpenChange,
}: {
  summary: ReactNode;
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [isOpen, setOpen] = useControllable<boolean>(open, defaultOpen, onOpenChange);
  const id = useId();
  return (
    <div className="k-disclosure">
      <button
        type="button"
        className="k-disclosure__summary k-layer k-t-label-lg"
        aria-expanded={isOpen}
        aria-controls={id}
        onClick={() => setOpen(!isOpen)}
      >
        <span>{summary}</span>
        <Icon icon={iconExpand} size={20} className="k-disclosure__chevron" />
      </button>
      <div id={id} role="region" hidden={!isOpen} className="k-disclosure__body">
        {children}
      </div>
    </div>
  );
}

type IconLinkOwn = {
  icon: IconData;
  label: string;
  variant?: 'standard' | 'tonal' | 'brand';
  /** Drawn after the icon, for example a badge. */
  children?: ReactNode;
};

/**
 * An icon button that goes somewhere: a link to a screen (pass the router's `Link` as `as`) or
 * to an address. It looks and sizes like `IconButton`.
 */
export function IconLink<T extends ElementType = 'a'>({
  as,
  icon,
  label,
  variant = 'standard',
  className,
  children,
  ...rest
}: IconLinkOwn & {
  as?: T;
  className?: string;
} & Omit<ComponentPropsWithoutRef<T>, keyof IconLinkOwn | 'as' | 'className'>) {
  const Component: ElementType = as ?? 'a';
  return (
    <Component
      {...rest}
      aria-label={label}
      className={cx('k-iconbtn k-layer', `k-iconbtn--${variant}`, className)}
    >
      <Icon icon={icon} />
      {children}
    </Component>
  );
}
